import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Archive, RotateCcw, Trash2, Package, CalendarDays, Receipt, FileText, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Archived = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('archived');
  const [loading, setLoading] = useState(true);
  const [archivedPackages, setArchivedPackages] = useState([]);
  const [archivedBookings, setArchivedBookings] = useState([]);
  const [archivedExpenses, setArchivedExpenses] = useState([]);
  const [archivedDocuments, setArchivedDocuments] = useState([]);
  const [trashItems, setTrashItems] = useState({ packages: [], bookings: [], expenses: [], documents: [] });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [packagesRes, bookingsRes, expensesRes, documentsRes, trashRes] = await Promise.all([
        axios.get(`${API}/packages/archived/list`, { headers }),
        axios.get(`${API}/bookings/archived/list`, { headers }),
        axios.get(`${API}/expenses/archived/list`, { headers }),
        axios.get(`${API}/documents/archived/list`, { headers }),
        axios.get(`${API}/trash`, { headers })
      ]);
      
      setArchivedPackages(packagesRes.data);
      setArchivedBookings(bookingsRes.data);
      setArchivedExpenses(expensesRes.data);
      setArchivedDocuments(documentsRes.data);
      setTrashItems(trashRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (type, id) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.patch(`${API}/${type}/${id}/unarchive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('archived.itemRestored'));
      fetchData();
    } catch (error) {
      toast.error(t('errors.somethingWentWrong'));
    }
  };

  const handleMoveToTrash = async (type, id) => {
    try {
      const token = localStorage.getItem('auth_token');
      // Different endpoint patterns for different types
      let endpoint;
      if (type === 'documents') {
        endpoint = `${API}/${type}/${id}/permanent`;
      } else if (type === 'bookings') {
        endpoint = `${API}/${type}/${id}/delete`;  // Bookings have /delete suffix
      } else {
        endpoint = `${API}/${type}/${id}`;  // Packages and expenses don't have /delete suffix
      }
      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('archived.itemDeleted'));
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(t('errors.failedToDelete'));
    }
  };

  const handleRestoreFromTrash = async (itemType, itemId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API}/trash/restore`, {
        item_type: itemType,
        item_id: itemId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('archived.itemRestored'));
      fetchData();
    } catch (error) {
      toast.error(t('errors.somethingWentWrong'));
    }
  };

  const handlePermanentDelete = async (itemType, itemId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API}/trash/${itemType}/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('archived.itemDeleted'));
      fetchData();
    } catch (error) {
      toast.error(t('errors.failedToDelete'));
    }
  };

  const handleEmptyTrash = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API}/trash/empty`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('archived.itemDeleted'));
      fetchData();
    } catch (error) {
      toast.error(t('errors.failedToDelete'));
    }
  };

  const totalArchived = archivedPackages.length + archivedBookings.length + archivedExpenses.length + archivedDocuments.length;
  const totalTrash = trashItems.packages.length + trashItems.bookings.length + trashItems.expenses.length + trashItems.documents.length;

  if (loading) {
    return <div className="text-muted-foreground">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-heading font-bold text-foreground">{t('archived.title')}</h1>
        <p className="text-muted-foreground mt-1">{t('archived.description')}</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="archived" className="flex items-center gap-2">
            <Archive className="w-4 h-4" />
            {t('archived.archivedItems')} ({totalArchived})
          </TabsTrigger>
          <TabsTrigger value="trash" className="flex items-center gap-2">
            <Trash2 className="w-4 h-4" />
            {t('archived.trashedItems')} ({totalTrash})
          </TabsTrigger>
        </TabsList>

        {/* Archived Tab */}
        <TabsContent value="archived" className="space-y-6 mt-6">
          {/* Archived Packages */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                {t('archived.archivedPackages')} ({archivedPackages.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {archivedPackages.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">{t('archived.noArchivedItems')}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('packages.packageId')}</TableHead>
                      <TableHead>{t('archived.clientName')}</TableHead>
                      <TableHead>{t('packages.saleValue')}</TableHead>
                      <TableHead>{t('archived.archived')}</TableHead>
                      <TableHead>{t('common.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archivedPackages.map((pkg) => (
                      <TableRow key={pkg.package_id}>
                        <TableCell className="font-mono text-sm">{pkg.package_id}</TableCell>
                        <TableCell>{pkg.client_name}</TableCell>
                        <TableCell>{pkg.currency} {pkg.sale_value?.toLocaleString()}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {pkg.archived_at ? new Date(pkg.archived_at).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell className="space-x-2">
                          <Button size="sm" variant="outline" onClick={() => handleUnarchive('packages', pkg.package_id)}>
                            <RotateCcw className="w-3 h-3 mr-1" />
                            {t('common.restore')}
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleMoveToTrash('packages', pkg.package_id)}>
                            <Trash2 className="w-3 h-3 mr-1" />
                            {t('common.delete')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Archived Bookings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5" />
                {t('archived.archivedBookings')} ({archivedBookings.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {archivedBookings.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">{t('archived.noArchivedItems')}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('bookings.bookingId')}</TableHead>
                      <TableHead>{t('packages.packageId')}</TableHead>
                      <TableHead>{t('bookings.serviceProvider')}</TableHead>
                      <TableHead>{t('common.cost')}</TableHead>
                      <TableHead>{t('common.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archivedBookings.map((booking) => (
                      <TableRow key={booking.booking_id}>
                        <TableCell className="font-mono text-sm">{booking.booking_id}</TableCell>
                        <TableCell>{booking.package_id}</TableCell>
                        <TableCell>{booking.service_provider}</TableCell>
                        <TableCell>{booking.currency || 'EUR'} {booking.booking_cost?.toLocaleString()}</TableCell>
                        <TableCell className="space-x-2">
                          <Button size="sm" variant="outline" onClick={() => handleUnarchive('bookings', booking.booking_id)}>
                            <RotateCcw className="w-3 h-3 mr-1" />
                            {t('common.restore')}
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleMoveToTrash('bookings', booking.booking_id)}>
                            <Trash2 className="w-3 h-3 mr-1" />
                            {t('common.delete')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Archived Expenses */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                {t('archived.archivedExpenses')} ({archivedExpenses.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {archivedExpenses.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">{t('archived.noArchivedItems')}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('expenses.expenseId')}</TableHead>
                      <TableHead>{t('common.description')}</TableHead>
                      <TableHead>{t('expenses.category')}</TableHead>
                      <TableHead>{t('common.amount')}</TableHead>
                      <TableHead>{t('common.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archivedExpenses.map((expense) => (
                      <TableRow key={expense.expense_id}>
                        <TableCell className="font-mono text-sm">{expense.expense_id}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{expense.description}</TableCell>
                        <TableCell><Badge variant="secondary">{expense.category}</Badge></TableCell>
                        <TableCell>€{expense.amount?.toFixed(2)}</TableCell>
                        <TableCell className="space-x-2">
                          <Button size="sm" variant="outline" onClick={() => handleUnarchive('expenses', expense.expense_id)}>
                            <RotateCcw className="w-3 h-3 mr-1" />
                            {t('common.restore')}
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleMoveToTrash('expenses', expense.expense_id)}>
                            <Trash2 className="w-3 h-3 mr-1" />
                            {t('common.delete')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Archived Documents */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {t('archived.archivedDocuments')} ({archivedDocuments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {archivedDocuments.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">{t('archived.noArchivedItems')}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('documents.documentId')}</TableHead>
                      <TableHead>{t('archived.filename')}</TableHead>
                      <TableHead>{t('expenses.category')}</TableHead>
                      <TableHead>{t('common.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archivedDocuments.map((doc) => (
                      <TableRow key={doc.document_id}>
                        <TableCell className="font-mono text-sm">{doc.document_id}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{doc.filename}</TableCell>
                        <TableCell><Badge variant="secondary">{doc.category}</Badge></TableCell>
                        <TableCell className="space-x-2">
                          <Button size="sm" variant="outline" onClick={() => handleUnarchive('documents', doc.document_id)}>
                            <RotateCcw className="w-3 h-3 mr-1" />
                            {t('common.restore')}
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleMoveToTrash('documents', doc.document_id)}>
                            <Trash2 className="w-3 h-3 mr-1" />
                            {t('common.delete')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trash Tab */}
        <TabsContent value="trash" className="space-y-6 mt-6">
          <Card className="bg-destructive/5 border-destructive/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-destructive" />
                  {t('archived.deleteConfirm')}
                </p>
                {totalTrash > 0 && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm">
                        <Trash2 className="w-4 h-4 mr-2" />
                        {t('archived.emptyTrash')}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t('archived.emptyTrash')}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t('archived.deleteConfirm')}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={handleEmptyTrash} className="bg-destructive text-destructive-foreground">
                          {t('archived.deleteForever')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Deleted Packages */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                {t('archived.deletedPackages')} ({trashItems.packages.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trashItems.packages.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">{t('archived.trashEmpty')}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('packages.packageId')}</TableHead>
                      <TableHead>{t('archived.clientName')}</TableHead>
                      <TableHead>{t('packages.saleValue')}</TableHead>
                      <TableHead>{t('archived.deleted')}</TableHead>
                      <TableHead>{t('common.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trashItems.packages.map((pkg) => (
                      <TableRow key={pkg.package_id}>
                        <TableCell className="font-mono text-sm">{pkg.package_id}</TableCell>
                        <TableCell>{pkg.client_name}</TableCell>
                        <TableCell>{pkg.currency} {pkg.sale_value?.toLocaleString()}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {pkg.deleted_at ? new Date(pkg.deleted_at).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell className="space-x-2">
                          <Button size="sm" variant="outline" onClick={() => handleRestoreFromTrash('packages', pkg.package_id)}>
                            <RotateCcw className="w-3 h-3 mr-1" />
                            {t('common.restore')}
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="destructive">
                                <Trash2 className="w-3 h-3 mr-1" />
                                {t('archived.deleteForever')}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{t('archived.permanentDelete')}?</AlertDialogTitle>
                                <AlertDialogDescription>{t('archived.deleteConfirm')}</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handlePermanentDelete('packages', pkg.package_id)} className="bg-destructive text-destructive-foreground">
                                  {t('common.delete')}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Deleted Bookings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5" />
                {t('archived.deletedBookings')} ({trashItems.bookings.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trashItems.bookings.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">{t('archived.trashEmpty')}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('bookings.bookingId')}</TableHead>
                      <TableHead>{t('packages.packageId')}</TableHead>
                      <TableHead>{t('bookings.serviceProvider')}</TableHead>
                      <TableHead>{t('common.cost')}</TableHead>
                      <TableHead>{t('common.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trashItems.bookings.map((booking) => (
                      <TableRow key={booking.booking_id}>
                        <TableCell className="font-mono text-sm">{booking.booking_id}</TableCell>
                        <TableCell>{booking.package_id}</TableCell>
                        <TableCell>{booking.service_provider}</TableCell>
                        <TableCell>{booking.currency || 'EUR'} {booking.booking_cost?.toLocaleString()}</TableCell>
                        <TableCell className="space-x-2">
                          <Button size="sm" variant="outline" onClick={() => handleRestoreFromTrash('bookings', booking.booking_id)}>
                            <RotateCcw className="w-3 h-3 mr-1" />
                            {t('common.restore')}
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="destructive">
                                <Trash2 className="w-3 h-3 mr-1" />
                                {t('archived.deleteForever')}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{t('archived.permanentDelete')}?</AlertDialogTitle>
                                <AlertDialogDescription>{t('archived.deleteConfirm')}</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handlePermanentDelete('bookings', booking.booking_id)} className="bg-destructive text-destructive-foreground">
                                  {t('common.delete')}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Deleted Expenses */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                {t('archived.deletedExpenses')} ({trashItems.expenses.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trashItems.expenses.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">{t('archived.trashEmpty')}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('expenses.expenseId')}</TableHead>
                      <TableHead>{t('common.description')}</TableHead>
                      <TableHead>{t('common.amount')}</TableHead>
                      <TableHead>{t('common.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trashItems.expenses.map((expense) => (
                      <TableRow key={expense.expense_id}>
                        <TableCell className="font-mono text-sm">{expense.expense_id}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{expense.description}</TableCell>
                        <TableCell>€{expense.amount?.toFixed(2)}</TableCell>
                        <TableCell className="space-x-2">
                          <Button size="sm" variant="outline" onClick={() => handleRestoreFromTrash('expenses', expense.expense_id)}>
                            <RotateCcw className="w-3 h-3 mr-1" />
                            {t('common.restore')}
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="destructive">
                                <Trash2 className="w-3 h-3 mr-1" />
                                {t('archived.deleteForever')}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{t('archived.permanentDelete')}?</AlertDialogTitle>
                                <AlertDialogDescription>{t('archived.deleteConfirm')}</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handlePermanentDelete('expenses', expense.expense_id)} className="bg-destructive text-destructive-foreground">
                                  {t('common.delete')}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Deleted Documents */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {t('archived.deletedDocuments')} ({trashItems.documents.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trashItems.documents.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">{t('archived.trashEmpty')}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('documents.documentId')}</TableHead>
                      <TableHead>{t('archived.filename')}</TableHead>
                      <TableHead>{t('common.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trashItems.documents.map((doc) => (
                      <TableRow key={doc.document_id}>
                        <TableCell className="font-mono text-sm">{doc.document_id}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{doc.filename}</TableCell>
                        <TableCell className="space-x-2">
                          <Button size="sm" variant="outline" onClick={() => handleRestoreFromTrash('documents', doc.document_id)}>
                            <RotateCcw className="w-3 h-3 mr-1" />
                            {t('common.restore')}
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="destructive">
                                <Trash2 className="w-3 h-3 mr-1" />
                                {t('archived.deleteForever')}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{t('archived.permanentDelete')}?</AlertDialogTitle>
                                <AlertDialogDescription>{t('archived.deleteConfirm')}</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handlePermanentDelete('documents', doc.document_id)} className="bg-destructive text-destructive-foreground">
                                  {t('common.delete')}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Archived;
